'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { cn } from '@ui/lib/utils';
import { useBecomeMentor } from '@website/hooks/useBecomeMentor';
import { useCurrentUser } from '@website/hooks/useCurrentUser';
import { buildLoginUrl } from '@website/utils/authCore';
import { getOnboardingRedirectPath } from '@website/utils/profileUtils';

const START_PARAM = 'start';

interface MentorLandingCtaProps {
  locale: string;
  label: string;
  loadingLabel: string;
  tone?: 'primary' | 'inverted';
  className?: string;
}

/**
 * One-click entry into professional onboarding. Anonymous visitors go through
 * login and come back with `?start=1`, which resumes the switch automatically
 * so the click they already made isn't lost.
 */
export function MentorLandingCta({ locale, label, loadingLabel, tone = 'primary', className }: MentorLandingCtaProps) {
  const router = useRouter();
  const { data: user, isLoading } = useCurrentUser();
  const { becomeMentor, isPending } = useBecomeMentor(locale);
  const resumed = useRef(false);

  const goToProfessionalSpace = () => {
    if (!user) return;
    router.push(`/${locale}${getOnboardingRedirectPath(user) ?? '/dashboard'}`);
  };

  const start = () => {
    if (!user) {
      const returnTo = new URL(window.location.href);
      returnTo.searchParams.set(START_PARAM, '1');
      returnTo.hash = '';
      window.location.href = buildLoginUrl({
        currentUrl: returnTo.toString(),
        origin: window.location.origin,
        locale
      });
      return;
    }
    if (user.isProfessional) {
      goToProfessionalSpace();
      return;
    }
    becomeMentor(user.id);
  };

  useEffect(() => {
    if (resumed.current || isLoading || !user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get(START_PARAM) !== '1') return;
    resumed.current = true;
    params.delete(START_PARAM);
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    start();
    // `start` closes over the latest user; re-running on its identity would re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, user]);

  return (
    <button
      type="button"
      onClick={start}
      disabled={isPending}
      className={cn(
        'inline-flex items-center justify-center rounded-full px-7 py-3.5 text-base font-semibold transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-70',
        tone === 'primary'
          ? 'bg-primary-500 text-white hover:bg-primary-600 focus-visible:ring-primary-500'
          : 'bg-white text-primary-600 hover:bg-primary-50 focus-visible:ring-white',
        'cursor-pointer',
        className
      )}
    >
      {isPending ? loadingLabel : label}
    </button>
  );
}
